import { Router } from 'express';
import { authMiddleware } from '../../middleware/auth.middleware';
import { requirePermission } from '../../middleware/rbac.middleware';
import { prisma } from '../../shared/prisma';
import { Response, NextFunction, Request } from 'express';
import { AuthRequest } from '../../middleware/auth.middleware';
import { getIo } from '../../realtime/socket.bootstrap';
import { SocketEvents } from '@ichat/shared-types';

const router = Router();
router.use(authMiddleware, requirePermission('view_audit_logs'));

// Dashboard stats
router.get('/stats', async (_req: Request, res: Response, next: NextFunction) => {
  try {
    const [users, activeUsers, teams, channels, messages] = await Promise.all([
      prisma.user.count(),
      prisma.user.count({ where: { presence: { not: 'offline' } } }),
      prisma.team.count(),
      prisma.channel.count({ where: { isArchived: false } }),
      prisma.message.count({ where: { isDeleted: false, createdAt: { gte: new Date(Date.now() - 24 * 60 * 60 * 1000) } } }),
    ]);
    res.json({ success: true, data: { users, activeUsers, teams, channels, messagesToday: messages } });
  } catch (err) { next(err); }
});

// Audit logs
router.get('/audit-logs', async (req: Request, res: Response, next: NextFunction) => {
  try {
    const page = Number(req.query.page) || 1;
    const take = 50;
    const logs = await prisma.auditLog.findMany({
      orderBy: { createdAt: 'desc' },
      take,
      skip: (page - 1) * take,
      include: { actor: { select: { id: true, displayName: true, email: true } } },
    });
    res.json({ success: true, data: logs });
  } catch (err) { next(err); }
});

// Chat logs
router.get('/chat-logs', async (req: Request, res: Response, next: NextFunction) => {
  try {
    const page = Number(req.query.page) || 1;
    const { startDate, endDate } = req.query;
    
    // If filtering by date, don't limit. Otherwise, limit to 1000 to prevent loading too much by default
    const take = (startDate || endDate) ? undefined : 1000;

    const where: any = {};
    if (startDate || endDate) {
      where.createdAt = {};
      if (startDate) where.createdAt.gte = new Date(startDate as string);
      if (endDate) {
        const end = new Date(endDate as string);
        end.setHours(23, 59, 59, 999);
        where.createdAt.lte = end;
      }
    }

    const messages = await prisma.message.findMany({
      where,
      orderBy: { createdAt: 'desc' },
      ...(take ? { take, skip: (page - 1) * take } : {}),
      select: {
        id: true,
        content: true,
        createdAt: true,
        isDeleted: true,
        senderId: true,
        channelId: true,
        conversationId: true,
        sender: { select: { id: true, displayName: true } },
        conversation: {
          select: {
            id: true,
            type: true,
            name: true,
            members: { select: { userId: true, user: { select: { id: true, displayName: true } } } }
          }
        },
        channel: { select: { id: true, name: true, team: { select: { name: true } } } },
        attachments: { select: { id: true, filename: true, fileSize: true, mimeType: true } }
      }
    });
    
    const formattedLogs = messages.map(m => {
      let to = 'Unknown';
      let type = 'Unknown';
      if (m.channelId && m.channel) {
        to = `${m.channel.team?.name || 'Team'} > ${m.channel.name}`;
        type = 'Channel';
      } else if (m.conversationId && m.conversation) {
        if (m.conversation.type === 'direct') {
          const otherMember = m.conversation.members.find(mem => mem.userId !== m.senderId);
          to = otherMember?.user?.displayName || 'Unknown';
          type = 'Direct Message';
        } else {
          to = m.conversation.name || 'Group Chat';
          type = 'Group';
        }
      }
      return {
        id: m.id,
        from: m.sender?.displayName || 'System',
        to,
        type,
        content: m.content,
        isDeleted: m.isDeleted,
        createdAt: m.createdAt,
        attachments: m.attachments,
      };
    });
    
    res.json({ success: true, data: formattedLogs });
  } catch (err) { next(err); }
});

// User management
router.get('/users', async (req: Request, res: Response, next: NextFunction) => {
  try {
    const users = await prisma.user.findMany({
      select: { 
        id: true, email: true, username: true, displayName: true, presence: true, isActive: true, createdAt: true,
        companyId: true, departmentId: true, position: true,
        company: { select: { id: true, name: true } },
        department: { select: { id: true, name: true } }
      },
      orderBy: { createdAt: 'desc' },
    });
    res.json({ success: true, data: users });
  } catch (err) { next(err); }
});

router.patch('/users/:id/toggle-active', async (req: Request, res: Response, next: NextFunction) => {
  try {
    const user = await prisma.user.findUnique({ where: { id: req.params.id } });
    if (!user) return res.status(404).json({ success: false, message: 'User not found' });
    const updated = await prisma.user.update({ where: { id: req.params.id }, data: { isActive: !user.isActive } });
    getIo()?.emit(SocketEvents.USER_UPDATED, { id: updated.id, isActive: updated.isActive });
    res.json({ success: true, data: { id: updated.id, isActive: updated.isActive } });
  } catch (err) { next(err); }
});

// Role management
router.get('/roles', async (_req: Request, res: Response, next: NextFunction) => {
  try {
    const roles = await prisma.role.findMany({ include: { _count: { select: { userRoles: true } } } });
    res.json({ success: true, data: roles });
  } catch (err) { next(err); }
});

router.post('/roles/assign', async (req: AuthRequest, res: Response, next: NextFunction) => {
  try {
    const { userId, roleId, scopeId, scopeType } = req.body;
    const userRole = await prisma.userRole.create({ data: { userId, roleId, scopeId, scopeType } });
    getIo()?.emit(SocketEvents.USER_UPDATED, { id: userId });
    res.json({ success: true, data: userRole });
  } catch (err) { next(err); }
});

export default router;
